import React, { useState, useEffect } from 'react';
import { Package, RefreshCw, AlertCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import PageContainer from '../layout/PageContainer';

interface StockItem {
  id: number;
  name: string;
  unit: string;
  category: string;
  current_stock: number;
  updated_at: string;
}

const SimpleStockView: React.FC = () => {
  const [stocks, setStocks] = useState<StockItem[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<StockItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStocks();
  }, []);

  useEffect(() => {
    filterStocks();
  }, [stocks, selectedCategory]);

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/simple-inventory/stocks');
      setStocks(res.data);
    } catch (error) {
      console.error('在庫取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStocks = () => {
    let filtered = stocks;

    // カテゴリフィルタのみ
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }

    setFilteredStocks(filtered);
  };

  const categories = ['all', ...Array.from(new Set(stocks.map(s => s.category || '未分類')))];

  const getStockStatusColor = (stock: number) => {
    if (stock <= 0) return 'text-red-600 bg-red-50';
    if (stock < 5) return 'text-orange-600 bg-orange-50';
    return 'text-green-600 bg-green-50';
  };

  const getStockStatusIcon = (stock: number) => {
    if (stock <= 0) return '❌';
    if (stock < 5) return '⚠️';
    return '✅';
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-6xl mx-auto p-6">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="text-blue-600" />
            📊 在庫確認
          </h1>
          <button
            onClick={fetchStocks}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={20} />
            更新
          </button>
        </div>

        {/* カテゴリフィルタ */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              📂 カテゴリ絞り込み:
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? '全てのカテゴリ' : cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 統計サマリー */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600 mb-1">全食材数</div>
            <div className="text-3xl font-bold text-gray-900">{stocks.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600 mb-1">在庫切れ</div>
            <div className="text-3xl font-bold text-red-600">
              {stocks.filter(s => s.current_stock <= 0).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600 mb-1">残り少ない (5以下)</div>
            <div className="text-3xl font-bold text-orange-600">
              {stocks.filter(s => s.current_stock > 0 && s.current_stock < 5).length}
            </div>
          </div>
        </div>

        {/* 在庫一覧 */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredStocks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-2 opacity-50" />
              <p>該当する在庫データがありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状態
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      食材名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      カテゴリ
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      現在在庫
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      最終更新
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredStocks.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStockStatusColor(item.current_stock)}`}>
                          {getStockStatusIcon(item.current_stock)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{item.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{item.category || '未分類'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`text-lg font-bold ${item.current_stock <= 0 ? 'text-red-600' : item.current_stock < 5 ? 'text-orange-600' : 'text-gray-900'}`}>
                          {item.current_stock.toFixed(1)} <span className="text-sm text-gray-600">{item.unit}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.updated_at ? new Date(item.updated_at).toLocaleString('ja-JP', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 注意書き */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="text-blue-600 mt-0.5" size={20} />
            <div className="text-sm text-blue-900">
              <p className="font-bold mb-1">💡 この画面について</p>
              <ul className="list-disc list-inside space-y-1">
                <li>全ての在庫が一覧で表示されます（表示のみ）</li>
                <li>カテゴリで絞り込むことができます</li>
                <li>POSレジで商品が売れると自動的に在庫が減ります</li>
                <li>仕入れ入力画面から在庫を追加できます</li>
                <li>在庫切れや残り少ない食材を一目で確認できます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default SimpleStockView;
